import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronDown,
  ChevronUp,
  Droplets,
  Zap,
  Brain,
  Activity,
  Shield,
  Users,
  BarChart3,
  ArrowRight,
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  Waves,
  Filter,
  Settings,
  Heart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import flowizLogo from '../../assets/flowiz-logo.png';

export default function LandingPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    agreeToTerms: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [openFaq, setOpenFaq] = useState(null);

  const navigate = useNavigate();


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://pixfi4xdviyjs26kwo2zxzz3ra0okekj.lambda-url.us-east-1.on.aws', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          mobile: formData.phone,
          description: formData.message
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit form');
      }

      setSubmitStatus('success');
      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        agreeToTerms: false
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "What makes Aquaflowz different from other water treatment design tools?",
      answer: "Aquaflowz combines advanced smart assistance with comprehensive process modeling to provide intelligent technology recommendations, parameter optimization, and real-time validation. Our platform supports all major water treatment technologies with an intuitive interface designed for professionals."
    },
    {
      question: "How accurate are the water treatment models in Aquaflowz?", 
      answer: "All our models are based on peer-reviewed research and industry data, validated against real-world plant performance. We provide real-time validation with instant feedback on parameter feasibility and automatic convergence monitoring to ensure accurate results."
    },
    {
      question: "Can I customize Aquaflowz for my specific water treatment needs?",
      answer: "Absolutely! Aquaflowz offers extensive customization including custom process configurations, personalized parameter sets, multi-project support, and integration with existing engineering workflows. Our smart assistant provides tailored recommendations based on your specific requirements."
    },
    {
      question: "What water treatment technologies does Aquaflowz support?",
      answer: "Aquaflowz supports all major water treatment processes including RO, FO, GAC, BSM2, MVC, Electrodialysis, Nanofiltration, and more. We also support hybrid systems, treatment trains, and custom process combinations with smart technology selection guidance."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 pb-20 sm:pb-40 px-4 sm:px-6 bg-white relative overflow-hidden">
        {/* Water-themed background elements */}
        <div className="absolute inset-0 opacity-5 hidden md:block">
          <div className="absolute top-20 left-20">
            <Droplets className="w-32 h-32 text-blue-500 transform rotate-12" />
          </div>
          <div className="absolute top-40 right-32">
            <Waves className="w-24 h-24 text-cyan-500 transform -rotate-12" />
          </div>
          <div className="absolute bottom-32 left-1/3">
            <Filter className="w-28 h-28 text-blue-600 transform rotate-45" />
          </div>
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center">
            <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
              className="mb-8 sm:mb-12"
            >
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-6 sm:mb-8 border border-blue-100">
                <Droplets className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Advanced Water Treatment Design Platform</span>
                <span className="sm:hidden">Water Treatment Platform</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Transform Water Treatment
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">Design & Optimization</span>
              </h1>
              
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
                Design optimal water treatment systems in minutes, not months. Get smart recommendations, 
                optimize parameters, and achieve better results with comprehensive process modeling.
              </p>
            </motion.div>

            <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center mb-12 sm:mb-16 px-4"
            >
              <button
                onClick={() => navigate('/flowsheets')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 sm:px-12 py-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 text-base sm:text-lg"
              >
                <Droplets className="w-5 h-5 sm:w-6 sm:h-6" />
                Get Started
              </button>
            </motion.div>

            {/* Minimalist Feature Icons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 max-w-3xl mx-auto px-4"
            >
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-full flex items-center justify-center mb-2 sm:mb-3 border border-blue-100">
                  <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">AI Assistant</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-50 rounded-full flex items-center justify-center mb-2 sm:mb-3 border border-cyan-100">
                  <Filter className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">Process Modeling</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-50 rounded-full flex items-center justify-center mb-2 sm:mb-3 border border-purple-100">
                  <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">Optimization</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-full flex items-center justify-center mb-2 sm:mb-3 border border-blue-100">
                  <Droplets className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">Water Treatment</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

                  {/* Features & Benefits Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 sm:mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 border border-blue-100">
              <Droplets className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Features & Benefits</span>
              <span className="sm:hidden">Features</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
              Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">Aquaflowz</span>?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
              Experience the future of water treatment design with smart assistance and comprehensive process modeling.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300 group border border-gray-100 text-left"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors duration-300">
                <Brain className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Smart Assistant</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Get intelligent recommendations for the best water treatment technologies based on your specific requirements 
                with smart flowsheet selection and parameter optimization.
              </p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300 group border border-gray-100 text-left"
            >
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-cyan-600 transition-colors duration-300">
                <Filter className="w-6 h-6 text-cyan-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Comprehensive Process Modeling</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Support for RO, FO, GAC, BSM2, MVC, Electrodialysis, and more with advanced optimization, 
                visual process diagrams, and real-time validation.
              </p>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300 group border border-gray-100 text-left"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-600 transition-colors duration-300">
                <Activity className="w-6 h-6 text-purple-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Advanced Optimization</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Built-in parameter sweeps and sensitivity analysis with real-time validation 
                and instant feedback on parameter feasibility and model convergence.
              </p>
            </motion.div>

            {/* Feature 4 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300 group border border-gray-100 text-left"
            >
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-600 transition-colors duration-300">
                <Settings className="w-6 h-6 text-green-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Professional-Grade Tools</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Configuration management, results analysis, multi-project support, and comprehensive 
                output comparison with data export capabilities.
              </p>
            </motion.div>

            {/* Feature 5 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300 group border border-gray-100 text-left"
            >
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-600 transition-colors duration-300">
                <BarChart3 className="w-6 h-6 text-orange-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Results Analysis</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Comprehensive output comparison and data export capabilities with detailed reporting 
                and analytics for optimal system performance insights.
              </p>
            </motion.div>

            {/* Feature 6 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300 group border border-gray-100 text-left"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors duration-300">
                <Droplets className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Modern User Experience</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Intuitive interface with clean, responsive design, cross-platform support, 
                real-time collaboration, and professional theming options.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Supported Water Treatment Technologies</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive support for all major water treatment processes and technologies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Membrane Processes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                  <Filter className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Membrane Processes</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Reverse Osmosis (RO)</li>
                <li>• Forward Osmosis (FO)</li>
                <li>• Osmotically Assisted RO (OARO)</li>
                <li>• Low Salinity RO (LSRRO)</li>
                <li>• Electrodialysis</li>
              </ul>
            </motion.div>

            {/* Adsorption & Filtration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                  <Waves className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Adsorption & Filtration</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Granular Activated Carbon (GAC)</li>
                <li>• Nanofiltration (NF)</li>
                <li>• Advanced Filtration Systems</li>
                <li>• Multi-Stage Filtration</li>
              </ul>
            </motion.div>

            {/* Biological Treatment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Biological Treatment</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• BSM2 (Benchmark Simulation Model)</li>
                <li>• Biological Nutrient Removal</li>
                <li>• Advanced Biological Processes</li>
                <li>• Wastewater Treatment</li>
              </ul>
            </motion.div>

            {/* Thermal Processes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center mr-3">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Thermal Processes</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Mechanical Vapor Compression (MVC)</li>
                <li>• Multi-Effect Distillation</li>
                <li>• Thermal Desalination</li>
                <li>• Energy Recovery Systems</li>
              </ul>
            </motion.div>

            {/* Hybrid Systems */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center mr-3">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Hybrid Systems</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Treatment Trains</li>
                <li>• Integrated Multi-Stage Processes</li>
                <li>• Energy Recovery Systems</li>
                <li>• Custom Process Combinations</li>
              </ul>
            </motion.div>

            {/* AI-Powered Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center mr-3">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Smart Features</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Smart Technology Selection</li>
                <li>• Parameter Optimization</li>
                <li>• Interactive Navigation</li>
                <li>• Expert Technology Guidance</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Can Benefit Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Who Can Benefit from Aquaflowz?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Designed for professionals across the water treatment industry.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Water Treatment Engineers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Water Treatment Engineers</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Design optimal treatment processes for specific water quality requirements</li>
                <li>• Optimize parameters for maximum efficiency and cost-effectiveness</li>
                <li>• Compare different technology options with quantitative analysis</li>
              </ul>
            </motion.div>

            {/* Research Scientists */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mr-4">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Research Scientists</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Develop and validate innovative new treatment processes effectively</li>
                <li>• Conduct comprehensive sensitivity analysis and parameter optimization studies</li>
                <li>• Publish results with professional documentation and research standards</li>
              </ul>
            </motion.div>

            {/* Consulting Firms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Consulting Firms</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Provide clients with data-driven treatment recommendations consistently</li>
                <li>• Generate professional reports with detailed process analysis capabilities</li>
                <li>• Demonstrate technology performance with validated modeling results</li>
              </ul>
            </motion.div>

            {/* Educational Institutions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mr-4">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Educational Institutions</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Teach advanced water treatment concepts using interactive tools</li>
                <li>• Provide students hands-on experience in professional process design</li>
                <li>• Conduct research projects with comprehensive industry-standard modeling</li>
              </ul>
            </motion.div>

            {/* Municipal Utilities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center mr-4">
                  <Droplets className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Municipal Utilities</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Optimize existing treatment facilities for improved regulatory compliance</li>
                <li>• Plan capacity expansions and technology upgrades systematically</li>
                <li>• Ensure performance standards with validated comprehensive models</li>
              </ul>
            </motion.div>

            {/* Industry Professionals */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Industry Professionals</h3>
              </div>
              <ul className="space-y-2 text-gray-600">
                <li>• Achieve process optimization and significant efficiency improvements</li>
                <li>• Evaluate and select optimal technologies for operations</li>
                <li>• Monitor performance with advanced analysis and reporting capabilities</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>


      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Side - FAQ Title */}
            <div>
              <h2 className="text-sm font-medium text-gray-500 mb-4">FAQ</h2>
              <h3 className="text-4xl font-bold text-gray-900 mb-4">Do you have any questions for us?</h3>
              <p className="text-gray-600">Do you have questions? We're here to provide comprehensive answers.</p>
            </div>

            {/* Right Side - FAQ Questions */}
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="border-b border-gray-200 pb-4"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left flex items-center justify-between group"
                  >
                    <span className="text-xl text-gray-900">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-6 h-6 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="mt-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section - After FAQs */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Ready to Transform Your Water Treatment Design?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <button
              onClick={() => navigate('/flowsheets')}
              className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200">
                Get Started Today
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-16 px-6 border-t">
        <div className="max-w-6xl mx-auto">
          {/* Large Logo */}
          <div className="text-center pt-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              <img 
                src={flowizLogo} 
                alt="Flowiz Logo" 
                className="w-16 h-16 object-contain"
              />
              <div className="text-6xl font-bold text-gray-900">Aquaflowz</div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
} 